import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';

import { centimosDe, teclear } from '#components/mobile/inicio/hoyPuedes';
import type { TeclaImporte } from '#components/mobile/inicio/hoyPuedes';

import { Icono } from './Icono';
import { color, radio } from './tokens';

const TECLAS: readonly TeclaImporte[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  ',',
  '0',
  'borrar',
];

type TecladoImporteProps = {
  onTecla: (tecla: TeclaImporte) => void;
  isDisabled?: boolean;
  /** Prefijo de los `data-testid` (`<prefijo>-teclado`, `<prefijo>-tecla-1`…). */
  testId?: string;
};

/**
 * Teclado de Rumbo para importes (nunca el del móvil): 1-9, coma, 0 y borrar.
 * Lo escrito se lleva con `teclear` y `centimosDe` de `hoyPuedes`.
 */
export function TecladoImporte({
  onTecla,
  isDisabled,
  testId,
}: TecladoImporteProps) {
  const { t } = useTranslation();
  return (
    <View
      data-testid={testId ? `${testId}-teclado` : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 6,
        width: '100%',
      }}
    >
      {TECLAS.map(k => (
        <Button
          key={k}
          variant="bare"
          isDisabled={isDisabled}
          aria-label={k === 'borrar' ? t('Delete') : k}
          data-testid={testId ? `${testId}-tecla-${k}` : undefined}
          onPress={() => onTecla(k)}
          style={{
            minHeight: 48,
            borderRadius: radio.boton,
            backgroundColor: color.surface2,
            color: color.fg,
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          {k === 'borrar' ? <Icono nombre="bksp" size={24} /> : k}
        </Button>
      ))}
    </View>
  );
}

/** «1234,5» a partir de céntimos, como lo dejaría el teclado. */
export function aEscrito(centimos: number): string {
  const enteros = Math.floor(centimos / 100);
  const resto = centimos % 100;
  if (resto === 0) {
    return String(enteros);
  }
  const decimales = String(resto).padStart(2, '0').replace(/0$/, '');
  return `${enteros},${decimales}`;
}

/**
 * Lo escrito con el teclado de Rumbo a partir de un importe inicial. Con un
 * importe ya puesto, la primera tecla lo sustituye (como un texto
 * seleccionado: `sustituir`); borrar lo deja vacío.
 */
export function useImporteTecleado(inicial: number | null) {
  const [escrito, setEscrito] = useState(() =>
    inicial ? aEscrito(Math.abs(inicial)) : '',
  );
  const [sustituir, setSustituir] = useState(!!inicial);
  return {
    escrito,
    centimos: centimosDe(escrito),
    sustituir,
    pulsar: (tecla: TeclaImporte) => {
      setEscrito(e => teclear(sustituir ? '' : e, tecla));
      setSustituir(false);
    },
    /** Pone un importe desde fuera (sugerencia), listo para sustituir. */
    poner: (centimos: number) => {
      setEscrito(centimos ? aEscrito(Math.abs(centimos)) : '');
      setSustituir(!!centimos);
    },
  };
}
